import { DateTime, Duration } from 'luxon';

const LOCALSTORAGE_KEY = 'ouronyx_api_cache';
const LOCALSTORAGE_VERSION_KEY = 'ouronyx_api_cache_version';
const VERSION = process.env.GATSBY_DEPLOY_ID ?? 'NO_VERSION';
const CACHE_LIFETIME = Duration.fromObject({ day: 1 });
type TCacheEntry = {
    timestamp: string;
    data: unknown;
};

type TCache = {
    [key: string]: TCacheEntry;
};

const getStorageKey = (secure?: boolean): string =>
    `${LOCALSTORAGE_KEY}${secure ? '_private' : ''}_${VERSION}`;

const jsStorage: Record<string, string> = {};

const getStorageItem = (key: string) => {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        console.error('Cannot access local storage');
        return jsStorage[key] ?? '';
    }
};

const setStorageItem = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        console.error('Cannot access local storage:', e);
        jsStorage[key] = value;
    }
};

const removeStorageItem = (key: string) => {
    try {
        return localStorage.removeItem(key);
    } catch (e) {
        console.error('Cannot access local storage');
        delete jsStorage[key];
    }
};

const getCache = (secure?: boolean): TCache => {
    return JSON.parse(getStorageItem(getStorageKey(secure)) ?? '{}');
};

const setCache = (newCache: TCache, secure?: boolean): void => {
    setStorageItem(getStorageKey(secure), JSON.stringify(newCache));
};

const isExpired = (cacheEntry: TCacheEntry): boolean => {
    return (
        DateTime.fromISO(cacheEntry.timestamp).plus(CACHE_LIFETIME).diffNow('second').seconds < 0
    );
};

const isNewBuild = (): boolean => {
    if (getStorageItem(LOCALSTORAGE_VERSION_KEY) !== VERSION) {
        for (const key in localStorage) {
            if (
                key.startsWith(LOCALSTORAGE_KEY) &&
                key !== LOCALSTORAGE_VERSION_KEY &&
                key !== getStorageKey() &&
                key !== getStorageKey(true)
            ) {
                removeStorageItem(key);
            }
        }

        setStorageItem(LOCALSTORAGE_VERSION_KEY, VERSION);

        return true;
    } else {
        return false;
    }
};

export const clearSecureCache = (): void => {
    removeStorageItem(getStorageKey(true));
};

export const clearCache = (): void => {
    removeStorageItem(getStorageKey());
};

export const clearAllCaches = (): void => {
    clearCache();
    clearSecureCache();
};

type TCachedData<P = unknown> = P | null;

/** Cached data for an endpoint, or null when missing, expired or from a previous build. */
export const readFromCache = <P = unknown>(endpoint: string, secure?: boolean): TCachedData<P> => {
    const cache = getCache(secure);

    if (!cache[endpoint] || isExpired(cache[endpoint]) || isNewBuild()) {
        return null;
    }

    return cache[endpoint].data as P;
};

export const saveToCache = (
    endpoint: string,
    data: unknown,
    secure?: boolean
): void => {
    // Settle the build marker first, so the next read doesn't discard this fresh entry as stale.
    isNewBuild();
    const cache = getCache(secure);
    cache[endpoint] = {
        timestamp: DateTime.local().toISO(),
        data
    };
    setCache(cache, secure);
};
